import { Grid, TextField, Typography } from '@material-ui/core';
import PlaceIcon from '@material-ui/icons/Place';
import Autocomplete from '@material-ui/lab/Autocomplete';
import throttle from 'lodash/throttle';
import React from 'react';
import { useGoogleMaps } from '~/hooks/useGoogleMaps';
import * as Sentry from '@sentry/react';

export default GoogleMapsAutocomplete;

const autocompleteService = { current: null };
const THROTTLE_TIME = 300;

function GoogleMapsAutocomplete(props) {
  const classes = {};
  /**
   * selectedPrediction a AutocompletePrediction from google maps
   * @link https://developers.google.com/maps/documentation/javascript/reference/places-autocomplete-service#AutocompletePrediction
   */
  const [selectedPrediction, setSelectedPrediction] = React.useState(null);
  const [inputValue, setInputValue] = React.useState('');
  const [options, setOptions] = React.useState([]);
  const GM = useGoogleMaps();
  //GM.googleMaps.places.PlacesServiceStatus.OK
  const OKStatus = 'OK';

  // create a debounced getPlacePredictions and keep it handy
  // (useMemo with an empty array calls the function once)
  const fetch = React.useMemo(
    () =>
      throttle((queryStr, callback) => {
        const request = { input: queryStr };
        autocompleteService.current.getPlacePredictions(request, (predictions, status) => {
          if (status === OKStatus) {
            // Only let street addresses and establishments (businesses) show in predictions -- get rid of country, city, state, route, natural_feature results because they are not specific
            // and the geocoder services does not return something that can be used as an address_1
            // While autocompleteService lets you restrict the type for getting predictions, it only allows one type which is too limiting
            // See https://developers.google.com/maps/documentation/geocoding/overview#Types for types
            const filteredPredictions = predictions.filter((prediction) => {
              if (prediction.types) {
                if (
                  prediction.types.includes('establishment') ||
                  prediction.types.includes('street_address') ||
                  prediction.types.includes('premise')
                ) {
                  if (!prediction.types.includes('natural_feature')) {
                    return true;
                  }
                }
              }
              return false;
            });
            return callback(filteredPredictions);
          } else {
            // eslint-disable-next-line no-console
            console.warn('googleMaps status not ok, it is', status);
            Sentry.captureException(new Error('Google Maps place predictions not Ok'));
          }
        });
      }, THROTTLE_TIME),
    []
  );

  // React to the user typing in the autocomplete text box
  React.useEffect(() => {
    let active = true;
    if (!autocompleteService.current && GM.isGoogleMapsReady) {
      autocompleteService.current = new GM.googleMaps.places.AutocompleteService();
    }
    if (!autocompleteService.current) {
      return undefined;
    }
    if (inputValue === '') {
      setOptions(selectedPrediction ? [selectedPrediction] : []);
      return undefined;
    }
    if (inputValue.length < 3) {
      return undefined;
    }
    fetch(inputValue, (results) => {
      if (active) {
        let newOptions = [];
        if (selectedPrediction) {
          newOptions = [selectedPrediction];
        }
        if (results) {
          newOptions = [...newOptions, ...results];
        }
        setOptions(newOptions);
      }
    });
    return () => {
      active = false;
    };
  }, [selectedPrediction, inputValue, fetch]);

  // React to the user selecting an autocomplete result from the dropdown
  React.useEffect(() => {
    async function getGeoLocation() {
      const place = await GM.getGeoLocation({ placeId: selectedPrediction.place_id });
      props.onPlaceSelected(place);
    }
    if (selectedPrediction) {
      getGeoLocation();
    }
  }, [selectedPrediction]);

  if (!GM.isGoogleMapsReady) {
    return null;
  }

  return (
    <Autocomplete
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.description)}
      filterOptions={(x) => x}
      options={options}
      autoComplete
      includeInputInList
      filterSelectedOptions
      value={selectedPrediction}
      onChange={(event, newValue) => {
        setOptions(newValue ? [newValue, ...options] : options);
        setSelectedPrediction(newValue);
      }}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label='Street Address'
          variant='outlined'
          fullWidth
          aria-label='Street Address'
        />
      )}
      renderOption={(option) => {
        return (
          <Grid container alignItems='center' role='menuitem' aria-label='Address Suggestion'>
            <Grid item>
              <PlaceIcon className={classes.icon} />
            </Grid>
            <Grid item xs>
              <Typography variant='body2' color='textPrimary'>
                {option.structured_formatting.main_text}
              </Typography>
              <Typography variant='body2' color='textSecondary'>
                {option.structured_formatting.secondary_text}
              </Typography>
            </Grid>
          </Grid>
        );
      }}
    />
  );
}
